import React, { useState, useRef } from 'react';
import cn from 'classnames';
import { Icon } from 'legacy-ft-cb';
import { OverviewIcon } from 'legacy-learnosity-player';
import { isMCQ } from 'legacy-src/utility/progressHelpers';
import {
  handleArrowMovement,
  handleSpaceAndEnter,
} from 'legacy-src/utility/accessibility';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import CharmBraceletPagination from '../charmBraceletPagination';
import './ScoringNav.scss';

const ICON_LIST_ITEM_CLASSNAME = 'question-icon-list-item';

function ScoringNav({
  questions,
  answeredQuestions = [],
  scoresByQuestion,
  currentQuestionIndex,
  goToQuestion = () => {},
  onQuestionClick = () => {},
  onPreviousClick,
  onNextClick,
  showIcons = true,
  showArrows = true,
  isListView = false,
  showDoubleArrow = true,
  onClose = () => {},
}) {
  const [focusedIndex, setFocusedIndex] = useState(currentQuestionIndex);
  const currentRef = useRef();

  const icons = questions.map((question, index) => {
    if (!showIcons) {
      return <span key={question.id} />;
    }

    const questionScore = scoresByQuestion[question.learnosityItemReference];
    const isAnswered = answeredQuestions.includes(
      question.learnosityItemReference,
    );
    const isScored =
      isMCQ(questions[index]) || (questionScore && questionScore.scored);
    const isSelected = currentQuestionIndex === index;
    const isLast = questions.length - 1 === index;
    const isFirst = index === 0;
    const { score = 0, maxScore = 0 } = questionScore || {};

    const icon = (() => {
      if (isNil(score) || !isScored) {
        return '';
      }
      if (score === 0 && maxScore > 0) {
        return 'incorrect';
      }
      if (score < maxScore) {
        return 'empty_circle';
      }
      if (score === maxScore && maxScore > 0) {
        return 'correct';
      }

      return '';
    })();

    const classes = cn('icon', {
      '--correct': icon === 'correct',
      '--partial': icon === 'empty_circle',
      '--incorrect': icon === 'incorrect',
    });

    const hasNextQuestionSameFeatureId =
      !isLast &&
      !isEmpty(
        intersection(question.featureIds, questions[index + 1].featureIds),
      );

    const hasPreviousQuestionSameFeatureId =
      !isFirst &&
      !isEmpty(
        intersection(question.featureIds, questions[index - 1].featureIds),
      );

    const hasConnection = hasNextQuestionSameFeatureId && !isLast;
    const hasPrevConnection = hasPreviousQuestionSameFeatureId;

    const onKeyUp = handleArrowMovement({
      right: () => {
        if (!isLast) {
          setFocusedIndex(focusedIndex + 1);
        }
      },
      left: () => {
        if (!isFirst) {
          setFocusedIndex(focusedIndex - 1);
        }
      },
      home: () => {
        setFocusedIndex(0);
      },
      end: () => {
        setFocusedIndex(questions.length - 1);
      },
    });

    if (index === focusedIndex && currentRef.current) {
      requestAnimationFrame(() => {
        currentRef.current.focus();
      });
    }

    const focusAndSwitch = (questionIndex) => {
      setFocusedIndex(questionIndex);
      goToQuestion(questionIndex);
    };

    const switchToQuestion = () => {
      goToQuestion(focusedIndex);
    };

    const onKeyDown = handleSpaceAndEnter({
      space: switchToQuestion,
      enter: switchToQuestion,
    });

    return (
      <span
        className={`${ICON_LIST_ITEM_CLASSNAME} ${
          isSelected ? 'is-selected' : ''
        }`}
        key={question.id}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        aria-selected={isSelected}
        aria-label={`Question ${index + 1}`}
        ref={currentRef}
        onClick={() => focusAndSwitch(index)}
      >
        <OverviewIcon
          isMobile={isListView}
          keyDownHandler={() => {}}
          index={index + 1}
          reference={question.learnosityItemReference}
          isAnswered={isAnswered}
          isSelected={isSelected}
          hasConnection={hasConnection}
          hasPrevConnection={hasPrevConnection}
          closeMenuContent={onQuestionClick}
          onClick={() => goToQuestion(index)}
          key={`icon-${question.id}`}
        />
        <span className="sublabel">
          <span>{isMCQ(questions[index]) ? 'MC' : ''}</span>
          <span className={classes}>
            <Icon name={icon} />
          </span>
        </span>
      </span>
    );
  });

  return (
    <CharmBraceletPagination
      showArrows={showArrows}
      showIcons={showIcons}
      isListView={isListView}
      tabIndex={0}
      role="tablist"
      showDoubleArrow={showDoubleArrow}
      iconsClassName={ICON_LIST_ITEM_CLASSNAME}
      currentQuestionIndex={currentQuestionIndex}
      goToQuestion={goToQuestion}
      onPreviousClick={onPreviousClick}
      onNextClick={onNextClick}
      focusedIndex={focusedIndex}
      onClose={onClose}
    >
      {icons}
    </CharmBraceletPagination>
  );
}

export default ScoringNav;
